/**
 * @fileoverview Renderer for existing DOM tables
 *
 * Enhances existing HTML tables with DataTables functionality.
 * Used when the table markup already exists in the DOM.
 *
 * @module renderers/DomTableRenderer
 */

import { BaseRenderer } from './BaseRenderer.js';
import { DEFAULTS } from '../constants.js';

/**
 * DomTableRenderer - Enhances existing HTML tables
 * @class
 * @extends BaseRenderer
 */
export class DomTableRenderer extends BaseRenderer {
  /**
   * Create a DomTableRenderer instance
   * @param {import('../types.js').RenderContext} context - Render context
   */
  constructor(context) {
    super(context);

    // Find existing table
    if (this.container.tagName === 'TABLE') {
      this.tableElement = this.container;
    } else {
      this.tableElement = this.container.querySelector('table');
    }

    if (!this.tableElement) {
      throw new Error('DomTableRenderer: No table element found in container');
    }

    this.dataTable = null;
  }

  /**
   * Render the existing DOM table with DataTables
   * @override
   * @returns {Promise<void>}
   */
  async render() {
    if (!this.isDataTablesAvailable()) {
      console.warn('DomTableRenderer: DataTables not available, table will remain static');
      this.isRendered = true;
      return;
    }

    this.startRenderTiming();

    // Apply modern styling
    this.applyModernStyling();

    // Build DataTables configuration
    const dtConfig = this._buildDataTablesConfig();

    try {
      // Initialize DataTable on existing table
      this.dataTable = jQuery(this.tableElement).DataTable(dtConfig);

      // Wrap table in scroll container so it can grow wider than the
      // controls without horizontally scrolling search/pagination too.
      this.wrapTableForScroll();

      this.endRenderTiming();
      this.isRendered = true;

      // Call init callback
      if (this.options.onInit) {
        this.options.onInit(this);
      }

    } catch (error) {
      console.error('DomTableRenderer: DataTable initialization failed:', error);
      this.showError('Failed to initialize table: ' + error.message);
      throw error;
    }
  }

  /**
   * Build DataTables configuration
   * @private
   * @returns {Object} DataTables config
   */
  _buildDataTablesConfig() {
    const config = {
      destroy: true,
      autoWidth: this.options.autoWidth !== undefined ? this.options.autoWidth : DEFAULTS.AUTO_WIDTH
    };

    // Only add options that are explicitly set
    if (this.options.pageLength !== undefined) {
      config.pageLength = this.options.pageLength;
    }
    if (this.options.lengthMenu !== undefined && this.options.lengthMenu !== null) {
      config.lengthMenu = this.options.lengthMenu;
    }
    if (this.options.searching !== undefined) {
      config.searching = this.options.searching;
    }
    if (this.options.ordering !== undefined) {
      config.ordering = this.options.ordering;
    }
    if (this.options.paging !== undefined) {
      config.paging = this.options.paging;
    }
    if (this.options.info !== undefined) {
      config.info = this.options.info;
    }
    if (this.options.dom !== undefined) {
      config.dom = this.options.dom;
    }
    if (this.options.order !== undefined) {
      config.order = this.options.order;
    }
    if (this.options.language !== undefined) {
      config.language = this.options.language;
    }
    if (this.options.columnDefs !== undefined) {
      config.columnDefs = this.options.columnDefs;
    }
    if (this.options.scrollY !== undefined) {
      config.scrollY = this.options.scrollY;
    }
    if (this.options.scrollCollapse !== undefined) {
      config.scrollCollapse = this.options.scrollCollapse;
    }
    if (this.options.select !== undefined && this.options.select !== null) {
      config.select = this.options.select;
    }
    if (this.options.buttons !== undefined) {
      config.buttons = this.options.buttons;
    }

    // Add callbacks
    config.drawCallback = this.options.drawCallback;

    // Wrap rowCallback to add row click handler
    if (this.options.onRowClick) {
      const originalRowCallback = this.options.rowCallback;
      config.rowCallback = (row, data, index) => {
        if (originalRowCallback) {
          originalRowCallback(row, data, index);
        }
        jQuery(row).on('click', () => {
          this.options.onRowClick(data, row, index);
        });
      };
    } else {
      config.rowCallback = this.options.rowCallback;
    }

    // Wrap initComplete for export buttons and user callback
    const userInitComplete = this.options.initComplete || this.options.fnInitComplete;
    config.initComplete = function(settings, json) {
      if (userInitComplete) {
        // Call with DataTables settings as 'this' context (provides this.api())
        userInitComplete.call(settings, settings, json);
      }
      // Export buttons would be added here by ExportManager
      if (this.options && this.options.onInitComplete) {
        this.options.onInitComplete(settings, json);
      }
    }.bind({ options: this.options });

    // Legacy callers configure the underlying DataTable with its native,
    // nested `dataTable: {...}` option shape (e.g. rowId, columns,
    // oLanguage) rather than this renderer's flat per-option keys above.
    // Nothing above reads that nested object, so those settings -- rowId
    // in particular, which callers rely on to give each <tr> a stable id
    // for later row lookups (e.g. row('[id=...]') to update one row after
    // an async check) -- were silently dropped, leaving every row without
    // the id its caller asked for. Merge it in last so it can override the
    // flat defaults, matching what the caller explicitly asked for.
    if (this.options.dataTable) {
      Object.assign(config, this.options.dataTable);
    }

    return config;
  }

  /**
   * Get DataTable instance
   * @returns {Object|null} DataTable instance
   */
  getDataTable() {
    return this.dataTable;
  }

  /**
   * Refresh table data
   */
  refresh() {
    if (this.dataTable) {
      this.dataTable.ajax.reload();
    }
  }

  /**
   * Clear table data
   */
  clear() {
    if (this.dataTable) {
      this.dataTable.clear().draw();
    }
  }

  /**
   * Search table
   * @param {string} term - Search term
   */
  search(term) {
    if (this.dataTable) {
      this.dataTable.search(term).draw();
    }
  }

  /**
   * Sort table
   * @param {number} columnIndex - Column index
   * @param {string} direction - Sort direction ('asc' or 'desc')
   */
  sort(columnIndex, direction) {
    if (this.dataTable) {
      this.dataTable.order([columnIndex, direction]).draw();
    }
  }

  /**
   * Get table data
   * @returns {Array}
   */
  getData() {
    if (this.dataTable) {
      return this.dataTable.rows().data().toArray();
    }
    return [];
  }

  /**
   * Get selected rows
   * @returns {Array}
   */
  getSelectedRows() {
    if (this.dataTable) {
      return this.dataTable.rows('.selected').data().toArray();
    }
    return [];
  }

  /**
   * Destroy renderer
   * @override
   */
  destroy() {
    if (this.dataTable) {
      try {
        this.dataTable.destroy();
      } catch (error) {
        console.warn('DomTableRenderer: Error destroying DataTable:', error);
      }
      this.dataTable = null;
    }

    super.destroy();
  }

  /**
   * Proxy methods to DataTable API
   */

  column(...args) {
    return this.dataTable ? this.dataTable.column(...args) : null;
  }

  columns(...args) {
    return this.dataTable ? this.dataTable.columns(...args) : null;
  }

  row(...args) {
    return this.dataTable ? this.dataTable.row(...args) : null;
  }

  rows(...args) {
    return this.dataTable ? this.dataTable.rows(...args) : null;
  }

  cell(...args) {
    return this.dataTable ? this.dataTable.cell(...args) : null;
  }

  cells(...args) {
    return this.dataTable ? this.dataTable.cells(...args) : null;
  }

  draw(...args) {
    return this.dataTable ? this.dataTable.draw(...args) : null;
  }

  page(...args) {
    return this.dataTable ? this.dataTable.page(...args) : null;
  }

  order(...args) {
    return this.dataTable ? this.dataTable.order(...args) : null;
  }
}

export default DomTableRenderer;
